// External
import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ModalRoutelessService } from '../components/ui/ui-modal-routeless/modal.routeless.service';
import { MessageService } from '../services/core/message.service';
import { UrlProperties } from '../services/guards/models/UrlProperties.model';
import { ValuesService } from '../values/values.service';

// Internal

@Injectable({
    providedIn: 'root'
})

/**
 * This service is used to avoid modal crash at redirect/refresh.
 * The problem occurs when navigation happens while a modal is opened (it ruins the url).
 * To avoid this, every time navigation happens, this service is used (insetead of router.navigate() use this.navigationService.navigate()).
 *
 * If a modal is oppened, an event is sent to ui-modal component with the destination path.
 * Ui-modal component saves the path and when the modal is closed, the user is redirected to the aformentioned destination path.
 * This service is not always necessary for navigation, it should be used only if there is a suspition that a redirect could happen while a modal is opened
 * and the desired behaviour is that the modal should stay opne until close (there are cases where navigation should still happen even if a modal 
 * is opened ex. user is no longer authenticated).
 *
 * The case where this service is needed is usually the one where redirect happenes from the code, with no user interation (ex. when a page is no 
 * longer available and the user must be redirected, when url params are cleaned and all the other cases where the user did not ineract with 
 * the inerface in order to navigate, did not push any butttons etc.).
 *
 * In that case, router.navigate should be replaced with this.navigationService.navigate()
 */


export class NavigationService {
    private isNavigationInProgress = true;

    constructor(
        private readonly router: Router,
        private readonly modalRoutelessService: ModalRoutelessService,
        private readonly valuesService: ValuesService,
        private readonly messageService: MessageService,
        private readonly activatedRoute: ActivatedRoute
    ) { }

    navigate(path, extras?) {
        if (this.modalRoutelessService.getIsModalOpened()) {
            if (path) {
                if (extras) {
                    this.messageService.sendMessage(this.valuesService.events.locationToRedirect, {path: path, extras: extras});
                } else {
                    this.messageService.sendMessage(this.valuesService.events.locationToRedirect, {path: path});
                }
            }
        } else {
            if (extras) {
                this.router.navigate([path], extras);
            } else {
                this.router.navigate([path]);
            }
        }
    }

    /**
     * Sets isNavigationInProgress flag
     * @param {boolean} value
     * @returns {void} sets flag
     */
    public setIsNavigationInProgress(value: boolean): void {
        this.isNavigationInProgress = value;
    }

    /**
     * Gets isNavigationInProgress flag
     * @returns {boolean}
     */
    public getIsNavigationInProgress(): boolean {
        return this.isNavigationInProgress;
    }

    /**
     * Function that forces same route reload for activating the navigation guard.
     * This way all needed requests and logic are remade
     */
    public forceRouteReload() {
        if (!this.isNavigationInProgress) {
            const activatedRouteSnapshot = this.activatedRoute.snapshot;
            const routeStateSnapshot = this.router.routerState.snapshot;
            const urlProperties:UrlProperties = this.processUrl(activatedRouteSnapshot, routeStateSnapshot);
            const _currentUrl = urlProperties.entirePath;
            const _queryParams = urlProperties.queryObject;
            this.router.navigate([ _currentUrl ], { queryParams: _queryParams });
        }
    }

    /**
     *
     *
     * @param {*} route Route snapshot
     * @param {*} state State snapshot
     * @return {*}
     * @memberof NavigationGuard
     */
     processUrl(route, state): UrlProperties {
        const entirePath = state.url.split('?')[0];
        const _navigationModalContainer = this.router.getCurrentNavigation()?.finalUrl?.root?.children?.modalContainer;

        let outletsString: string, hasOutlet: boolean, outletsObject: object;
        if(state.url.split('(')[1]) {
            outletsString = `(${state.url.split('(')[1].split(')')[0]})`;
            outletsObject = {
                container: state.url.split('(')[1].split(':')[0],
                value: state.url.split('(')[1].split(':')[1].split(')')[0]
            };
            hasOutlet = true;
        } else if (_navigationModalContainer) {
            outletsObject = {
                container: 'modalContainer',
                value: _navigationModalContainer.segments[0].path
            };
            outletsString = `(modalContainer:${_navigationModalContainer.segments[0].path})`;
            hasOutlet = true;
        }
        else {
            outletsString = '';
            hasOutlet = false;
            outletsObject = {};
        }

        const _getUrlSegments = (path: string) => {
            const segments = [];
            const splittedPath = path.split('/');
            for(let i = 1; i < splittedPath.length; i++) {
                segments.push(splittedPath[i]);
            }
            return segments;
        };

        return {
            fullUrl: state.url,
            queryString: state.url.split('?')[1],
            queryObject: route.queryParams,
            pathPieces: _getUrlSegments(entirePath),
            urlParams: route.params,
            entirePath,
            outletsObject,
            outletsString,
            hasOutlet
        };
    }
}
